import * as Yup from 'yup';
import { Form, Row, Collapse, Accordion, Table } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import LocalizedStrings from 'react-localization';
import { ClientLanguage, getClientLanguageAsString } from "../../../../../Api";
import { CustomAccordionButton, CustomFormControl, CustomFileControlButton, CustomFileControlText, CustomLink } from '../../../../StyledComponents';
import { useState, useEffect, useRef } from 'react';
import { characterRestrictionInDocNamesRegExp } from '../ValidationRules';

let locStrings = new LocalizedStrings({
    de:
    {
        filePermittedFileFormats: "Folgende Dateiformate sind erlaubt: {permittedExtensions}",
        fileInvalidFileName: "Ungültiger Dokumentenname",
        overallDocumentsSelectionHint: "Insgesamt können maximal {fileNumberLimit} Dateien mit zusammen höchstens {fileSizeLimit} ausgewählt werden.",

        documentFileRequired: "Dokument ist erforderlich und darf nicht größer als {fileSizeLimit} sein.",
        documentFileSizeError: "Dokument darf nicht größer als {fileSizeLimit} sein.",
        documentFileEmptyError: "Die Datei darf nicht leer sein.",
        documentFileButtonText: "Datei auswählen",
        documentFilePlaceholder: "Keine ausgewählt",

        multipleDocumentsFilesRequirements: "Die Unterlagen dürfen höchstens {fileNumberLimit} Dateien und zusammen nicht größer als {fileSizeLimit} sein.",
        multipleDocumentsFilesHint: "Mehrfachauswahl möglich",
        multipleDocumentsFilesButtonText: "Dateien auswählen",
        multipleDocumentsFilesPlaceholder: "Keine ausgewählt",
        multipleDocumentsFilesMultipleFilesSelected: " Dateien",
        multipleDocumentsAtLeastOne: "Mindestens ein Dokument ist erforderlich.",

        letterFile: "Anschreiben*",

        cvFile: "Lebenslauf*",

        halfYearCertificatesFiles: "Letzten beiden Halbjahreszeugnisse*",

        additionalFiles: "Weitere Unterlagen (optional)",

        filesSelectedFiles: "Ausgewählte Dateien ",
        filesDeleteFile: "Entfernen",
    },
    en:
    {
        filePermittedFileFormats: "The following file formats are allowed: {permittedExtensions}",
        fileInvalidFileName: "Invalid document name",
        overallDocumentsSelectionHint: "Overall, a maximum of {fileNumberLimit} files with a maximum total size of {fileSizeLimit} can be selected.",

        documentFileRequired: "Document is required and must not be larger than {fileSizeLimit}.",
        documentFileSizeError: "Document must not be larger than {fileSizeLimit}.",
        documentFileEmptyError: "The file must not be empty.",
        documentFileButtonText: "Browse...",
        documentFilePlaceholder: "No file selected.",

        multipleDocumentsFilesRequirements: "The documents must be no more than {fileNumberLimit} files and together no larger than {fileSizeLimit}.",
        multipleDocumentsFilesHint: "Multiple selection possible",
        multipleDocumentsFilesButtonText: "Browse...",
        multipleDocumentsFilesPlaceholder: "No files selected.",
        multipleDocumentsFilesMultipleFilesSelected: " files selected.",
        multipleDocumentsAtLeastOne: "At least one document is required.",

        letterFile: "Cover letter*",

        cvFile: "CV*",

        halfYearCertificatesFiles: "Last two half-year certificates*",

        additionalFiles: "Additional documents (optional) ",

        filesSelectedFiles: "Selected files ",
        filesDeleteFile: "Remove",
    }
});

const applicationPermittedExtensions = ['.jpeg', '.jpg', '.png', '.pdf']
const applicationPermittedMimeTypes = ['image/jpeg', 'image/png', 'application/pdf']

const customFileNumberLimit2 = 2;

function getFileSizeAsString(totalBytes: number): string {
    const Gigabyte = 1073741824
    const Megabyte = 1048576
    const Kilobyte = 1024

    if (totalBytes <= 0) return "0 Bytes"

    if (totalBytes >= Gigabyte) {
        return (totalBytes / Gigabyte).toFixed(2) + " GB"
    } else if (totalBytes >= Megabyte) {
        return (totalBytes / Megabyte).toFixed(2) + " MB"
    } else if (totalBytes >= Kilobyte) {
        return (totalBytes / Kilobyte).toFixed(2) + " kB"
    } else {
        return totalBytes + " Bytes"
    }
}

function setLanguage(clientLanguage): void {
    switch(clientLanguage) { 
        case ClientLanguage.German: { 
           locStrings.setLanguage('de');
           break; 
        } 
        case ClientLanguage.English: { 
           locStrings.setLanguage('en'); 
           break; 
        } 
        // Otherwise "clientLanguage" is "ClientLanguage.BrowserSetting" and language used is browser setting dependent.
    } 
}

const checkRequiredDokument = (settings, clientLanguage) => (Yup.mixed()
    .when("InformationIsRequired", {
        is: () => true,
        then: (schema) => {
            setLanguage(clientLanguage);
            return schema.required(locStrings.formatString(locStrings.documentFileRequired, { fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString());
          },
    })
    .when("CheckFileSize", {
        is: () => true,
        then: (schema) => {
            setLanguage(clientLanguage);
            return schema.test("FileSize", locStrings.formatString(locStrings.documentFileSizeError, { fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString(), (file) => file && file.size < settings.applicationFileSizeLimit);
          },
    })
    .when("CheckNullByteTest", {
        is: () => true,
        then: (schema) => {
            setLanguage(clientLanguage);
            return schema.test("NullByteTest", locStrings.documentFileEmptyError, (file) => {
                    if (file) {
                        if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
                        file.isValid = file.size > 0;
                        if (!file.isValid) file.errMessage = locStrings.documentFileEmptyError;
                        return file.isValid;
                    }
                });
          },
    })
    .when("CheckFileFormat", {
        is: () => true,
        then: (schema) => {
            setLanguage(clientLanguage);
            return schema.test("FileFormat", locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString(), (file) => {
                    if (file) {
                        if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
                        file.isValid = applicationPermittedMimeTypes.includes(file.type);
                        if (!file.isValid) file.errMessage = locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString();
                        return file.isValid;
                    }
                });
          },
    })
    .when("Check.settings.isUnicode", {
        is: () => true,
        then: (schema) => {
            setLanguage(clientLanguage);
            return schema.when("settings.isUnicode", {
                    is: () => !settings.isUnicode,
                    then: Yup.mixed()
                        .test("FileNameTest",
                            locStrings.fileInvalidFileName,
                            (file) => {
                                if (file) {
                                    if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
                                    file.isValid = file.name.match(characterRestrictionInDocNamesRegExp) !== null;
                                    if (!file.isValid) file.errMessage = locStrings.fileInvalidFileName;
                                    return file.isValid;
                                }
                            })
                });
          }
    })
    );

const checkDokument = (settings, clientLanguage) => (Yup.mixed()
    .when("CheckFileSize", {
        is: () => true,
        then: (schema) => {
            setLanguage(clientLanguage);
            return schema.test("FileSize", locStrings.formatString(locStrings.documentFileSizeError, { fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString(), (file) => {
                    if (file) 
                    {
                        if (file.isValid === undefined || file.isValid === null) 
                        {
                            file.isValid = true;
                        }
                        else
                        {
                            file.isValid = file.size < settings.applicationFileSizeLimit;
                            if (!file.isValid) file.errMessage = locStrings.formatString(locStrings.documentFileSizeError, { fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString();
                        }
                        return file.isValid;
                    } 
                    else 
                    {
                        return true;
                    }
                });
          },
    })
    .when("CheckNullByteTest", {
        is: () => true,
        then: (schema) => {
            setLanguage(clientLanguage);
            return schema.test("NullByteTest", locStrings.documentFileEmptyError, (file) => {
                    if (file) 
                    {
                        if (file.isValid === undefined || file.isValid === null) 
                        {
                            file.isValid = true;
                        }
                        else if (file.isValid) 
                        {
                            file.isValid = file.size > 0;
                            if (!file.isValid) file.errMessage = locStrings.documentFileEmptyError;
                        }
                        return file.isValid;
                    } 
                    else 
                    {
                        return true;
                    }
                });
          },
    })
    .when("CheckFileFormat", {
        is: () => true,
        then: (schema) => {
            setLanguage(clientLanguage);
            return schema.test("FileFormat", locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString(), (file) => {
                    if (file) 
                    {
                        if (file.isValid === undefined || file.isValid === null) 
                        {
                            file.isValid = true;
                        }
                        else if (file.isValid) 
                        {
                            file.isValid = applicationPermittedMimeTypes.includes(file.type);
                            if (!file.isValid) file.errMessage = locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString();
                        }
                        return file.isValid;
                    } 
                    else 
                    {
                        return true;
                    }
                });
          },
    })
    .when("Check.settings.isUnicode", {
        is: () => true,
        then: (schema) => {
            setLanguage(clientLanguage);
            return schema.when("settings.isUnicode", {
                    is: () => !settings.isUnicode,
                    then: Yup.mixed()
                        .test("FileNameTest",
                            locStrings.fileInvalidFileName,
                            (file) => {
                                if (file) 
                                {
                                    if (file.isValid === undefined || file.isValid === null) 
                                    {
                                        file.isValid = true;
                                    }
                                    else if (file.isValid) 
                                    {
                                        file.isValid = file.name.match(characterRestrictionInDocNamesRegExp) !== null;
                                        if (!file.isValid) file.errMessage = locStrings.fileInvalidFileName;
                                    }
                                    return file.isValid;
                                } 
                                else 
                                {
                                    return true;
                                }
                            })
                });
          },
    })
    );

export const Schema = (settings, clientLanguage) => (Yup.object().shape({
    letterFile: checkRequiredDokument(settings, clientLanguage),
    cvFile: checkRequiredDokument(settings, clientLanguage),
    halfYearCertificatesFiles: Yup.array()
        .when("InformationIsRequired", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.test("Required",
                        locStrings.multipleDocumentsAtLeastOne,
                        (files) => {
                            if (files && files.length > 0) {
                                return true;
                            } else {
                                return false;
                            }
                    });
            },
        })
        .when("CheckFileSize", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.test("FileSize",
                        locStrings.formatString(locStrings.multipleDocumentsFilesRequirements, { fileNumberLimit: customFileNumberLimit2, fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString(),
                        (files) => {
                            if (files && files.length > 0) {
                                let totalSize: number = files.reduce((accumulator, currentFile) => accumulator + currentFile.size, 0);
                                return totalSize <= settings.applicationFileSizeLimit && files.length <= customFileNumberLimit2;
                            } else {
                                return true;
                            }
                    });
            },
        })
        .when("CheckFileFormat", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.test("FileFormat",
                        locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString(),
                        (files) => {
                            if (files && files.length > 0) {
                                let allValid = true;
                                for (let file of files) {
                                    if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                                    if (!applicationPermittedMimeTypes.includes(file.type)) {
                                        file.isValid = false;
                                        file.errMessage = locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString();
                                        allValid = false;
                                    }
                                }
                                return allValid;
                            } else {
                                return true;
                            }
                    });
            },
        })
        .when("Check.settings.isUnicode", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.when("settings.isUnicode", {
                        is: () => !settings.isUnicode,
                        then: Yup.array().nullable()
                            .test("FileNameTest",
                                locStrings.fileInvalidFileName,
                                (files) => {
                                    if (files && files.length > 0) {
                                        let allValid = true;
                                        for (let file of files) {
                                            if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                                            if (!file.name.match(characterRestrictionInDocNamesRegExp)) {
                                                file.isValid = false;
                                                file.errMessage = locStrings.fileInvalidFileName;
                                                allValid = false;
                                            }
                                        }
                                        return allValid;
                                    } else {
                                        return true;
                                    }
                                })
                    });
            },
        }),
    additionalFiles: Yup.array()
        .nullable()
        .when("CheckFileSize", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.test("FileSize",
                        locStrings.formatString(locStrings.multipleDocumentsFilesRequirements, { fileNumberLimit: settings.applicationFileNumberLimit, fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString(),
                        (files) => {
                            if (files && files.length > 0) {
                                let totalSize: number = files.reduce((accumulator, currentFile) => accumulator + currentFile.size, 0);
                                return totalSize <= settings.applicationFileSizeLimit && files.length <= settings.applicationFileNumberLimit;
                            } else {
                                return true;
                            }
                    });
                },
        })
        .when("CheckFileFormat", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.test("FileFormat",
                        locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString(),
                        (files) => {
                            if (files && files.length > 0) {
                                let allValid = true;
                                for (let file of files) {
                                    if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                                    if (!applicationPermittedMimeTypes.includes(file.type)) {
                                        file.isValid = false;
                                        file.errMessage = locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString();
                                        allValid = false;
                                    }
                                }
                                return allValid;
                            } else {
                                return true;
                            }
                    });
                },
        })
        .when("Check.settings.isUnicode", {
            is: () => true,
            then: (schema) => {
                setLanguage(clientLanguage);
                return schema.when("settings.isUnicode", {
                        is: () => !settings.isUnicode,
                        then: Yup.array().nullable()
                            .test("FileNameTest",
                                locStrings.fileInvalidFileName,
                                (files) => {
                                    if (files && files.length > 0) {
                                        let allValid = true;
                                        for (let file of files) {
                                            if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                                            if (!file.name.match(characterRestrictionInDocNamesRegExp)) {
                                                file.isValid = false;
                                                file.errMessage = locStrings.fileInvalidFileName;
                                                allValid = false;
                                            }
                                        }
                                        return allValid;
                                    } else {
                                        return true;
                                    }
                                })
                    });
                },
        }),
}));

class FileExt extends File {
    isValid: boolean = true;
    errMessage: string = "";
}

export const DocumentsSubForm = ({ form: { values, setFieldTouched, setFieldValue, handleBlur, handleChange, touched, errors }, ...props }) => {
    const { colors, settings, clientLanguage } = props;
    
    const fileInputLetter = useRef<HTMLInputElement>(null);
    const fileInputCV = useRef<HTMLInputElement>(null);
    const fileInputHalfYearCertificatesFiles = useRef<HTMLInputElement>(null);
    const fileInputAdditionalFiles = useRef<HTMLInputElement>(null);
    const [fileNameLetter, setFileNameLetter] = useState(locStrings.documentFilePlaceholder);
    const [fileNameCV, setFileNameCV] = useState(locStrings.documentFilePlaceholder);
    const [fileNamesHalfYearCertificatesFiles, setFileNamesHalfYearCertificatesFiles] = useState(locStrings.documentFilePlaceholder);
    const [fileNamesAdditionalFiles, setFileNamesAdditionalFiles] = useState(locStrings.multipleDocumentsFilesPlaceholder);

    const [letterDocument, setLetterDocument] = useState<FileExt>();
    const [cvDocument, setCvDocument] = useState<FileExt>();
    const [halfYearCertificatesDocuments, setHalfYearCertificatesDocuments] = useState<FileExt[]>([]);
    const [additionalDocuments, setAdditionalDocuments] = useState<FileExt[]>([]);

    setLanguage(clientLanguage);

    useEffect(() => {
        setFileNameLetter(locStrings.documentFilePlaceholder);
        setFileNameCV(locStrings.documentFilePlaceholder);
        setFileNamesHalfYearCertificatesFiles(locStrings.documentFilePlaceholder);
        setFileNamesAdditionalFiles(locStrings.multipleDocumentsFilesPlaceholder);
    }, []);

    function getClientLanguage(): string { 
        return getClientLanguageAsString(clientLanguage); 
    } 
    
    function getTotalSizeOfHalfYearCertificatesDocs(): number {
        if (!halfYearCertificatesDocuments || halfYearCertificatesDocuments?.length === 0) return 0

        let totalSize: number = 0
        for (let i = 0; i < halfYearCertificatesDocuments.length; i++) {
            const element = halfYearCertificatesDocuments[i];
            totalSize += element.size
        }
        return totalSize
    }

    function getTotalSizeOfAdditionalDocs(): number {
        if (!additionalDocuments || additionalDocuments?.length === 0) return 0

        let totalSize: number = 0
        for (let i = 0; i < additionalDocuments.length; i++) {
            const element = additionalDocuments[i];
            totalSize += element.size
        }
        return totalSize
    }

    function getTotalSizeOfDocs(): number {
        let totalSizeOfHalfYearCertificatesDocs = getTotalSizeOfHalfYearCertificatesDocs();
        let totalSizeOfAddititionDocs = getTotalSizeOfAdditionalDocs();
        let letterDocumentSize = letterDocument ? letterDocument.size : 0;
        let cvDocumentSize = cvDocument ? cvDocument.size : 0;
        return totalSizeOfHalfYearCertificatesDocs + totalSizeOfAddititionDocs + cvDocumentSize + letterDocumentSize;
    }

    return (
        <Row>
            <Form.Group className="mb-3" controlId="formLetterDoc">
                <Form.Label className={"FormLetterFileText"}>{locStrings.letterFile} {letterDocument && "(" + getFileSizeAsString(letterDocument.size) + ")"} </Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    name="letterFile"
                    ref={fileInputLetter}
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setLetterDocument(event.target.files[0]);
                            setFieldTouched("letterFile");
                            setFieldValue("letterFile", event.target.files[0] || undefined);
                            setFileNameLetter(event.target.files[0].name);
                        }
                    }}
                    style={{ display: 'none' }}
                    isValid={touched.letterFile && !errors.letterFile}
                    isInvalid={touched.letterFile && errors.letterFile}
                    accept={applicationPermittedExtensions}
                />
                <InputGroup className="mb-3">
                    <CustomFileControlButton className={"FormLetterFileControlButton"}
                        onClick={() => fileInputLetter.current && fileInputLetter.current.click()}
                        colors={colors}
                        // isValid={touched.letterFile && !errors.letterFile}
                        // isInvalid={touched.letterFile && errors.letterFile}
                        >
                        {locStrings.documentFileButtonText}
                    </CustomFileControlButton>
                    <CustomFileControlText className={"FormLetterFileControlText"}
                        onClick={() => fileInputLetter.current && fileInputLetter.current.click()}
                        colors={colors}
                        value={fileNameLetter}
                        type="text"
                        isValid={touched.letterFile && !errors.letterFile}
                        isInvalid={touched.letterFile && errors.letterFile}
                        readOnly
                    />
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                    {errors.letterFile}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: touched.letterFile && errors.letterFile ? "none" : "flex" }}>
                    {locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString()}
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCvDoc">
                <Form.Label className={"FormCVFileText"}>{locStrings.cvFile} {cvDocument && "(" + getFileSizeAsString(cvDocument.size) + ")"} </Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    required
                    name="cvFile"
                    ref={fileInputCV}
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setCvDocument(event.target.files[0]);
                            setFieldTouched("cvFile");
                            setFieldValue("cvFile", event.target.files[0] || undefined);
                            setFileNameCV(event.target.files[0].name);
                        }
                    }}
                    style={{ display: 'none' }}
                    isValid={touched.cvFile && !errors.cvFile}
                    isInvalid={touched.cvFile && errors.cvFile}
                    accept={applicationPermittedExtensions}
                />
                <InputGroup className="mb-3">
                    <CustomFileControlButton className={"FormCVFileControlButton"}
                        onClick={() => fileInputCV.current && fileInputCV.current.click()}
                        colors={colors}
                        // isValid={touched.cvFile && !errors.cvFile}
                        // isInvalid={touched.cvFile && errors.cvFile}
                        >
                        {locStrings.documentFileButtonText}
                    </CustomFileControlButton>
                    <CustomFileControlText className={"FormCVFileControlText"}
                        onClick={() => fileInputCV.current && fileInputCV.current.click()}
                        colors={colors}
                        value={fileNameCV}
                        type="text"
                        isValid={touched.cvFile && !errors.cvFile}
                        isInvalid={touched.cvFile && errors.cvFile}
                        readOnly
                    />
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                    {errors.cvFile}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: touched.cvFile && errors.cvFile ? "none" : "flex" }}>
                    {locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString()}
                </Form.Text>                
            </Form.Group>

            <Form.Group className="mb-3" controlId="formHalfYearCertificatesFiles">
                <Form.Label className={"FormHalfYearCertificatesFilesText"}>{locStrings.halfYearCertificatesFiles} {halfYearCertificatesDocuments && getTotalSizeOfHalfYearCertificatesDocs() > 0 && "(" + getFileSizeAsString(getTotalSizeOfHalfYearCertificatesDocs()) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    multiple
                    name="halfYearCertificatesFiles"
                    ref={fileInputHalfYearCertificatesFiles}
                    onChange={(event) => {

                        if (event.target.files?.length) {
                            let files: FileExt[] = [];
                            if (halfYearCertificatesDocuments.length > 0) {
                                files = halfYearCertificatesDocuments.slice();
                            }
                            files.push(...Array.from<FileExt>(event.target.files));

                            setHalfYearCertificatesDocuments(files);
                            setFieldTouched("halfYearCertificatesFiles");
                            setFieldValue("halfYearCertificatesFiles", files || undefined);
                            let numberOfFiles = event.target.files.length;
                            if (numberOfFiles == 1) {
                                setFileNamesHalfYearCertificatesFiles(event.target.files[0].name);
                            }
                            if (numberOfFiles > 1) {
                                setFileNamesHalfYearCertificatesFiles(numberOfFiles.toString() + locStrings.multipleDocumentsFilesMultipleFilesSelected);
                            }
                        }

                    }}
                    style={{ display: 'none' }}
                    isValid={touched.halfYearCertificatesFiles && !errors.halfYearCertificatesFiles}
                    isInvalid={touched.halfYearCertificatesFiles && errors.halfYearCertificatesFiles}
                    accept={applicationPermittedExtensions} 
                />
                <InputGroup className="mb-3">
                    <CustomFileControlButton className={"FormHalfYearCertificatesFilesControlButton--ChooseFileButton"}
                        onClick={() => fileInputHalfYearCertificatesFiles.current && fileInputHalfYearCertificatesFiles.current.click()}
                        colors={colors}
                        // isValid={touched.halfYearCertificatesFiles && !errors.halfYearCertificatesFiles}
                        // isInvalid={touched.halfYearCertificatesFiles && errors.halfYearCertificatesFiles}
                        >
                        {locStrings.multipleDocumentsFilesButtonText}
                    </CustomFileControlButton>
                    <CustomFileControlText className={"FormHalfYearCertificatesFilesControlText form-control-static FormLastNameControl"}
                        onClick={() => fileInputHalfYearCertificatesFiles.current && fileInputHalfYearCertificatesFiles.current.click()}
                        colors={colors}
                        value={fileNamesHalfYearCertificatesFiles}
                        type="text"
                        isValid={touched.halfYearCertificatesFiles && !errors.halfYearCertificatesFiles}
                        isInvalid={touched.halfYearCertificatesFiles && errors.halfYearCertificatesFiles}
                        readOnly
                    />
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                    {errors.halfYearCertificatesFiles}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: touched.halfYearCertificatesFiles && errors.halfYearCertificatesFiles ? "none" : "flex" }}>
                    {locStrings.multipleDocumentsFilesHint}. {locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString()}
                </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formAdditionalFiles">
                <Form.Label className={"FormAdditionalFilesText"}>{locStrings.additionalFiles} {additionalDocuments && getTotalSizeOfAdditionalDocs() > 0 && "(" + getFileSizeAsString(getTotalSizeOfAdditionalDocs()) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    multiple
                    name="additionalFiles"
                    ref={fileInputAdditionalFiles}
                    onChange={(event) => {

                        if (event.target.files?.length) {
                            let files: FileExt[] = [];
                            if (additionalDocuments.length > 0) {
                                files = additionalDocuments.slice();
                            }
                            files.push(...Array.from<FileExt>(event.target.files));

                            setAdditionalDocuments(files);
                            setFieldTouched("additionalFiles");
                            setFieldValue("additionalFiles", files || undefined);
                            let numberOfFiles = event.target.files.length;
                            if (numberOfFiles == 1) {
                                setFileNamesAdditionalFiles(event.target.files[0].name);
                            }
                            if (numberOfFiles > 1) {
                                setFileNamesAdditionalFiles(numberOfFiles.toString() + locStrings.multipleDocumentsFilesMultipleFilesSelected);
                            }
                        }

                    }}
                    style={{ display: 'none' }}
                    isValid={touched.additionalFiles && !errors.additionalFiles}
                    isInvalid={errors.additionalFiles}
                    accept={applicationPermittedExtensions} 
                />
                <InputGroup className="mb-3">
                    <CustomFileControlButton className={"FormAdditionalFilesControlButton--ChooseFileButton"}
                        onClick={() => fileInputAdditionalFiles.current && fileInputAdditionalFiles.current.click()}
                        colors={colors}
                        // isValid={touched.additionalFiles && !errors.additionalFiles}
                        // isInvalid={touched.additionalFiles && errors.additionalFiles}
                        >
                        {locStrings.multipleDocumentsFilesButtonText}
                    </CustomFileControlButton>
                    <CustomFileControlText className={"FormAdditionalFilesControlText form-control-static FormLastNameControl"}
                        onClick={() => fileInputAdditionalFiles.current && fileInputAdditionalFiles.current.click()}
                        colors={colors}
                        value={fileNamesAdditionalFiles}
                        type="text"
                        isValid={touched.additionalFiles && !errors.additionalFiles}
                        isInvalid={touched.additionalFiles && errors.additionalFiles}
                        readOnly
                    />
                </InputGroup>
                <Form.Control.Feedback type="invalid">
                    {errors.additionalFiles}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: errors.additionalFiles ? "none" : "flex" }}>
                    {locStrings.multipleDocumentsFilesHint}. {locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: applicationPermittedExtensions.join(" ") }).toString()}
                </Form.Text>
            </Form.Group>

            <Form.Text muted className="mb-3" >
                {locStrings.formatString(locStrings.overallDocumentsSelectionHint, { fileNumberLimit: settings.applicationFileNumberLimit, fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString()}
            </Form.Text>

            <div style={{ marginBottom: "16px" }}>
                <Collapse in={getTotalSizeOfDocs() > 0}>
                    <Accordion>
                        <Accordion.Item eventKey="0">
                            <CustomAccordionButton colors={colors}>{locStrings.filesSelectedFiles}({getFileSizeAsString(getTotalSizeOfDocs())})</CustomAccordionButton>
                            <Accordion.Body>
                                <Table>
                                    <tbody>
                                    {letterDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={letterDocument.isValid ? "" : "text-danger"}><strong>{letterDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(letterDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setLetterDocument(undefined);
                                                        setFieldTouched("letterFile");
                                                        setFieldValue("letterFile", undefined);

                                                        let inputElement = document.getElementById("formLetterDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                        setFileNameLetter(locStrings.documentFilePlaceholder);
                                                    }}
                                                >{locStrings.filesDeleteFile}</CustomLink></td>
                                                <td className="invalid" title={letterDocument.errMessage} style={{ visibility: letterDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {cvDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={cvDocument.isValid ? "" : "text-danger"}><strong>{cvDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(cvDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCvDocument(undefined);
                                                        setFieldTouched("cvFile");
                                                        setFieldValue("cvFile", undefined);

                                                        let inputElement = document.getElementById("formCvDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                        setFileNameCV(locStrings.documentFilePlaceholder);
                                                    }}
                                                >{locStrings.filesDeleteFile}</CustomLink></td>
                                                <td className="invalid" title={cvDocument.errMessage} style={{ visibility: cvDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {halfYearCertificatesDocuments && Array.from(halfYearCertificatesDocuments).map((file: FileExt) =>
                                            <tr key={file.name}>
                                                <td style={{ wordBreak: "break-all" }} className={file.isValid === undefined || file.isValid ? "" : "text-danger"}>{file.name}</td>
                                                <td>{getFileSizeAsString(file.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        let pos = -1;
                                                        for (let i = 0; i < halfYearCertificatesDocuments.length; i++) {
                                                            const f: FileExt = halfYearCertificatesDocuments[i]
                                                            if (f.name === file.name) {
                                                                pos = i;
                                                                break;
                                                            }
                                                        }

                                                        if (pos > -1) {
                                                            let files: FileExt[] = [];
                                                            files = halfYearCertificatesDocuments.slice();
                                                            files.splice(pos, 1);

                                                            setHalfYearCertificatesDocuments(files);
                                                            setFieldValue("halfYearCertificatesFiles", files || undefined);

                                                            if (files.length === 0) {
                                                                let inputElement = document.getElementById("formHalfYearCertificatesFiles") as HTMLInputElement;
                                                                inputElement.value = "";
                                                                setFileNamesHalfYearCertificatesFiles(locStrings.multipleDocumentsFilesPlaceholder);
                                                            }
                                                        }
                                                    }}
                                                >{locStrings.filesDeleteFile}</CustomLink></td>
                                                <td className="invalid" title={file.errMessage} style={{ visibility: file.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        )}

                                        {additionalDocuments && Array.from(additionalDocuments).map((file: FileExt) =>
                                            <tr key={file.name}>
                                                <td style={{ wordBreak: "break-all" }} className={file.isValid === undefined || file.isValid ? "" : "text-danger"}>{file.name}</td>
                                                <td>{getFileSizeAsString(file.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        let pos = -1;
                                                        for (let i = 0; i < additionalDocuments.length; i++) {
                                                            const f: FileExt = additionalDocuments[i]
                                                            if (f.name === file.name) {
                                                                pos = i;
                                                                break;
                                                            }
                                                        }

                                                        if (pos > -1) {
                                                            let files: FileExt[] = [];
                                                            files = additionalDocuments.slice();
                                                            files.splice(pos, 1);

                                                            setAdditionalDocuments(files);
                                                            setFieldValue("additionalFiles", files || undefined);

                                                            if (files.length === 0) {
                                                                let inputElement = document.getElementById("formAdditionalFiles") as HTMLInputElement;
                                                                inputElement.value = "";
                                                                setFileNamesAdditionalFiles(locStrings.multipleDocumentsFilesPlaceholder);
                                                            }
                                                        }
                                                    }}
                                                >{locStrings.filesDeleteFile}</CustomLink></td>
                                                <td className="invalid" title={file.errMessage} style={{ visibility: file.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Collapse>
            </div>
        </Row>
    );
};
